/**
 * The POS entry point — the composition root.
 *
 * Master plan §4 M4, §3 K8, §3 K10.
 *
 * Everything with a rule in it lives elsewhere and is tested there. This file
 * only decides which implementations get wired together, which is exactly the
 * job a composition root should have: swapping the in-memory outbox for the
 * OPFS one, or a stub menu for the replicated one, is a change here and
 * nowhere else.
 */

import { createApp, h, reactive, ref, shallowRef } from "vue";
import { Translator, type Catalog } from "@gf/kernel/i18n";
import { cssVariables, type DensityName, type ThemeName } from "@gf/design";
/*
 * The typeface and the shared layer, FIRST and in this order.
 *
 * `fonts.css` before `base.css` so the `@font-face` rules are registered before
 * anything asks for the family. Both before every component's scoped styles,
 * which Vite emits in import order — so a component rule and a `gf-` rule of
 * equal specificity resolve in favour of the component, which is what makes a
 * per-file migration onto the shared layer possible without a big-bang commit.
 */
import "@gf/ui/fonts.css";
import "@gf/ui/base.css";
import {
  MemoryOutboxStore,
  Outbox,
  createDrainLoop,
  createUploader,
  SqliteOutboxStore,
  sellState,
  type DegradationReason,
} from "@gf/sync-client";
import { createOpfsDriver } from "@gf/sync-client/opfs";
import { SqliteDrawerJournal } from "./drawer-journal";
import { SqliteReceiptLeaseStore } from "./receipt-lease-store";
import { SqliteCredentialStore } from "./credential-store";
import { SqliteOfflineCache } from "./offline-cache";
import { SqliteOrderJournal } from "./order-journal";
import { ReceiptNumbers } from "./receipt-numbers";
import { NumberingClient } from "./numbering-client";
import { PosSession, type CheckView, type MenuSnapshot } from "./session";
import type { StaleShift } from "./shift-join";
import { businessDateOf } from "@gf/kernel/time";
import {
  type AllowanceReasonCode,
  type ModeChangeReasonCode,
  type VoidReasonCode,
} from "./locales/codes";
import {
  loadCatalogs,
  loadDeferredCatalogs,
  FALLBACK_LOCALE,
  AVAILABLE_LOCALES,
} from "./locales/load";
import { UpdateGate } from "./update-gate";
import { fetchAvailability, fetchMenu, normalizeMenu } from "./menu-client";
import { holdSignIn, releaseSignIn, restoreSignIn, shouldLock, touchSignIn } from "./held-sign-in";
import { canApprove, needsApproval, type ApprovalAction } from "./approvals";
import type { MenuChoice } from "@gf/module-catalog/choices";
import { syncNumbers as syncNumbers_ } from "./numbers-sync";
import { mintGuestToken } from "@gf/module-guest-channels";
import {
  ConfigFetchError,
  createConfigCache,
  fetchConfigBundle,
  readVenueConfig,
  type VenueConfig,
} from "./config-client";
import { CheckBook, PICKUP, tableOf, type SessionFactory } from "./checkbook";
import type { ReadyEntry } from "./service-ready";
import type { FloorBooking } from "./bookings";
import type { PickupNumbers } from "./pickup-numbers";
import { forgetPairing, pairingCodeFromHash, readPairing, type DevicePairing } from "@gf/sync-client/pairing";
import { scopeLocalData, takeOverLocalData, unsentEvents } from "@gf/sync-client/local-owner";
import { ShiftSession, enrolOnDevice, fetchRoster } from "./shift-client";

import { DrawerSession } from "./drawer";
import type { DenominationCount } from "@gf/module-cash/shift";
import PosScreen from "./ui/PosScreen.vue";
import type { ReceiptBook } from "./receipts-client";
import type { ReceiptsController } from "./receipts-controller";
import type { TenderKind } from "@gf/module-orders";

// --- Theme -----------------------------------------------------------------

/**
 * Tokens as custom properties on the root, so a theme switch is one attribute:
 * no re-render, no second bundle, no flash of the wrong colours.
 */
function applyTheme(theme: ThemeName, density: DensityName): void {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const style = document.createElement("style");
  style.dataset["gfTokens"] = "";
  style.textContent = `:root {\n${cssVariables(theme, density, reducedMotion)}\n}`;

  document.querySelector("style[data-gf-tokens]")?.remove();
  document.head.appendChild(style);
}

const preferredTheme = (): ThemeName =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

// --- Language --------------------------------------------------------------

/**
 * At most two catalogues: the fallback, and the negotiated language.
 *
 * All four used to be in the startup chunk — 31 kB of translations for a till
 * that reads 8 kB of them, and a tax every future language would have levied on
 * every device in every country. The chunks are precached by the service
 * worker, so this is "not at startup" rather than "needs a network".
 */
/**
 * THE CHOSEN LANGUAGE OUTRANKS THE DEPLOYMENT'S.
 *
 * `loadCatalogs` already takes a pin that beats the device's preferences, for
 * the deployment. Somebody standing at the till who has picked a language is a
 * stronger signal still, and feeding it in HERE rather than switching after
 * mount is what stops the screen rendering in Czech and then flipping.
 */
const LOCALE_KEY = "gf.pos.locale";

const { locale, catalogs } = await loadCatalogs(
  navigator.languages,
  localStorage.getItem(LOCALE_KEY) ?? import.meta.env["VITE_GF_LOCALE"],
);

const buildTranslator = (of: readonly Catalog[], at: string): Translator =>
  new Translator([...of], {
    locale: at,
    // Czech, not English: this is a Czech product and an English fallback in
    // front of a Czech server is worse than a missing string.
    fallbackLocale: FALLBACK_LOCALE,
    onMissing: (key, missingIn) => {
      // Reported rather than swallowed. A gap nobody sees is a gap that ships.
      console.warn(`i18n: missing "${key}" in ${missingIn}`);
    },
  });

/**
 * THE LANGUAGE CAN CHANGE WITHOUT A RELOAD.
 *
 * The deployment pins the language and that is right — the browser is the
 * wrong authority for a till, and a Czech venue's handheld should be in Czech
 * whatever the tablet was configured with. But a venue hires, and the person
 * holding the handheld tonight may read Ukrainian far better than Czech.
 *
 * Held in a ref and read through a getter in the root props, because Vue
 * evaluates root props ONCE, eagerly: a plain variable here would be captured
 * at mount and the screen would never change. That mistake has already shipped
 * twice in this repository, in two different apps.
 *
 * `shallowRef`, NOT `ref`, AND THAT IS NOT A PERFORMANCE CHOICE.
 *
 * `ref` on an object calls `reactive` on it, which returns a Proxy. `Translator`
 * keeps its catalogues in a `#private` field, and a private-field access
 * through a Proxy fails the brand check — every single call to `t.t(...)` threw
 *
 *     TypeError: Receiver must be an instance of class anonymous
 *
 * and the till rendered a blank page. `shallowRef` stores the instance as it
 * is; only the reference is tracked, which is all that has to change when
 * somebody picks a language. Any class with private fields put in a `ref` will
 * do the same thing, silently, at the first method call.
 */
const translatorRef = shallowRef(buildTranslator(catalogs, locale));
const localeRef = ref<string>(locale);
const heldCatalogs = new Map<string, Catalog>([
  [FALLBACK_LOCALE, catalogs[0]!],
  [locale, catalogs[catalogs.length - 1]!],
]);

async function setLocale(code: string): Promise<void> {
  if (code === localeRef.value) return;
  // Lazy, because a tap on a two-letter button is not first paint. See the
  // module for the rule this follows.
  const { switchLocale } = await import("./locale-switch");
  await switchLocale(code, {
    held: heldCatalogs,
    build: buildTranslator,
    apply: (translator, at) => {
      translatorRef.value = translator;
      localeRef.value = at;
      document.documentElement.lang = at;
    },
    remember: (at) => localStorage.setItem(LOCALE_KEY, at),
  });
}

// --- The session -----------------------------------------------------------

/**
 * Durable, on OPFS.
 *
 * This was `MemoryOutboxStore`, which meant a reload lost every queued sale and
 * — once shifts existed — the open drawer with them. The composition root was
 * always the only place that had to change; `SqliteOutboxStore` and its SQL are
 * the same ones the hub and the tests run over bun:sqlite.
 *
 * IN-MEMORY IS STILL THE FALLBACK, and that is a deliberate choice rather than
 * defensive habit. If OPFS is unavailable — a browser too old, private
 * browsing, storage denied — a till that refused to start would be a venue that
 * cannot trade. One that keeps selling and loses the queue on reload is worse
 * in a way nobody sees, so `sellState` is told, and the banner says this device
 * cannot store sales.
 */
const storage = await (async () => {
  try {
    const driver = createOpfsDriver();
    const store = new SqliteOutboxStore(driver);
    await store.migrate();
    // The same database as the outbox: same durability settings, and nothing
    // second to open. It is a local read model — nothing in it is ever sent.
    const journal = new SqliteDrawerJournal(driver);
    await journal.migrate();
    // Same database again, for the same reasons — and here durability is not a
    // nicety: the cursor must survive a crash BETWEEN being advanced and the
    // event being emitted, which is what turns a lost number into an explained
    // gap instead of a duplicate receipt.
    const leases = new SqliteReceiptLeaseStore(driver);
    await leases.migrate();
    // Same database again, for the staff verifiers this device has minted.
    // Without it a reload threw them away and the next sign-in needed the
    // network — on a till whose whole premise is verifying a PIN locally.
    const credentials = new SqliteCredentialStore(driver);
    await credentials.migrate();
    // And the two things a till needs to SELL: the floor plan and the menu.
    // Without them a reload during an outage boots, signs somebody in, and
    // leaves them looking at nothing to ring.
    const cache = new SqliteOfflineCache(driver);
    await cache.migrate();
    // And the open checks themselves. Without it a reload during an outage
    // boots, signs somebody in, shows them the floor plan — and shows every
    // table free, with four of them sitting in front of an unpaid bill.
    const orders = new SqliteOrderJournal(driver);
    await orders.migrate();
    return { driver, store, journal, leases, credentials, cache, orders, durable: true as const };
  } catch (error) {
    console.error("outbox: OPFS is unavailable, falling back to memory —", error);
    return {
      driver: undefined,
      store: new MemoryOutboxStore(),
      journal: undefined,
      leases: undefined,
      credentials: undefined,
      cache: undefined,
      orders: undefined,
      durable: false as const,
    };
  }
})();


const outbox = new Outbox(storage.store, { batchSize: 50, maxAttempts: 3 });


/**
 * The menu, empty until the first fetch lands.
 *
 * Reactive so the grid fills in when it arrives, rather than requiring the
 * server to answer before the till will render at all. A POS that blocks its
 * own startup on a network call is one that cannot open during an outage.
 */
const menuState = reactive<{ current: MenuSnapshot }>({
  current: { version: 0, resolvedAt: new Date(0), categories: [], items: [] },
});

/**
 * Where this till is, and who it is.
 *
 * From the build-time environment for now. Stage C's device claim replaces it
 * with a signed config bundle and a staff PIN; until then these are explicit
 * placeholders rather than the silent "staff:unknown" that used to be
 * stamped onto every event in the log.
 */
/**
 * WHO THIS DEVICE IS. The build says only where the API is and which key a
 * config bundle must be signed with; the venue, the device's id, its key and
 * its secret come from PAIRING — a code from the office, claimed once and kept
 * on the device. A development build that names them in its environment (the
 * demo does) is paired already. No build carries a venue's secret.
 */
const pairingStore = ((): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();
const apiBase = import.meta.env["VITE_GF_API"] ?? "http://localhost:3000";
/**
 * A pairing LINK (`#pair=123456`, from the office's QR or the demo launcher)
 * outranks the stored pairing: somebody asked for this device to become
 * another one. Taken out of the address bar at once, so a reload does not
 * try to spend it again.
 */
const linkedCode = pairingCodeFromHash(location.hash);
if (linkedCode) history.replaceState(null, "", location.pathname + location.search);
// A link opened in a tab that is already running is only a fragment change.
addEventListener("hashchange", () => {
  if (pairingCodeFromHash(location.hash)) location.reload();
});
const paired: DevicePairing =
  (import.meta.env["VITE_GF_API_KEY"]
    ? {
        apiKey: import.meta.env["VITE_GF_API_KEY"],
        tenantId: import.meta.env["VITE_GF_TENANT"] ?? "11111111-1111-4111-8111-111111111111",
        venueId: import.meta.env["VITE_GF_VENUE"] ?? "33333333-3333-4333-8333-333333333333",
        deviceId: import.meta.env["VITE_GF_DEVICE"] ?? "44444444-4444-4444-8444-444444444444",
        deviceSecret: import.meta.env["VITE_GF_DEVICE_SECRET"] ?? "development-device-secret",
        kind: "till" as const,
        stationId: null,
        name: "development till",
      }
    : null) ??
  (linkedCode ? null : readPairing(pairingStore, "till")) ??
  (await (
    await import("./pairing-screen")
  ).pairThisDevice({
    baseUrl: apiBase,
    storage: pairingStore,
    copyFrom: { catalogs, more: () => loadDeferredCatalogs(locale), build: buildTranslator, locale },
    kind: "till",
    copy: "pos",
    linkedCode,
  }));

/**
 * THE LOCAL DATABASE BELONGS TO THIS PAIRING. Re-paired elsewhere (training →
 * the real venue, or a demo reset), the old venue's leases, drawer and floor
 * must not come along — and unsent sales block the change rather than vanish.
 */
if (storage.driver) {
  const driver = storage.driver;
  const owned = await scopeLocalData(driver, paired);
  if (owned.kind === "WIPED") {
    location.reload();
    await new Promise<never>(() => {});
  }
  if (owned.kind === "BLOCKED") {
    const t = buildTranslator([...catalogs, ...(await loadDeferredCatalogs(locale))], locale);
    await (await import("@gf/ui/unsent-screen")).showUnsentBlock({
      words: {
        title: t.t("pos.unsent.title"),
        lead: t.t("pos.unsent.lead", { count: owned.unsent }),
        downloadLabel: t.t("pos.unsent.download"),
        discardLabel: t.t("pos.unsent.discard"),
        confirmLead: t.t("pos.unsent.confirmLead"),
        confirmLabel: t.t("pos.unsent.confirm"),
        cancelLabel: t.t("pos.unsent.cancel"),
      },
      exportJson: async () => JSON.stringify({ previous: owned.previous, events: await unsentEvents(driver) }, null, 2),
      fileName: `gastrofirst-unsent-${owned.previous.deviceId}.json`,
      discard: () => takeOverLocalData(driver, paired),
    });
  }
}

const config = {
  baseUrl: apiBase,
  apiKey: paired.apiKey,
  venueId: paired.venueId,
  deviceId: paired.deviceId,
  tenantId: paired.tenantId,
  deviceSecret: paired.deviceSecret,
  /**
   * The config verifying key, PINNED at build time.
   *
   * Pinned rather than fetched: a key that arrives alongside the bundle it
   * authenticates authenticates nothing. The development default matches the
   * development signing key in apps/api/src/config-signing.ts.
   */
  configPublicKey:
    import.meta.env["VITE_GF_CONFIG_KEY"] ?? "9fEParSCh_bMwrXt7EzSHI9QeqY8aMe4iPPHqg7sq8A",
} as const;

/**
 * Who is holding the handheld.
 *
 * Null until somebody signs in, and the till shows the login screen until it is
 * not. Every event the POS emitted before this carried `staff:unassigned` —
 * which makes void-abuse detection and the fraud statistics an attribution that
 * every row shares, and therefore no attribution at all.
 */
const signedIn = reactive<{ staffId: string | null; displayName: string | null; roleId: string | null }>({
  staffId: null,
  displayName: null,
  roleId: null,
});

/**
 * A reload does not sign anybody out; ten idle minutes do. See `held-sign-in.ts`.
 */
const deviceStorage = ((): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();
{
  const held = restoreSignIn(deviceStorage, Date.now());
  if (held) {
    signedIn.staffId = held.staffId;
    signedIn.displayName = held.displayName;
    signedIn.roleId = held.roleId;
  }
}

const actorId = (): string => signedIn.staffId ?? "staff:unassigned";

/**
 * The shift, and the device secret its verifiers are salted with.
 *
 * The secret is build-time here. Real device provisioning (AMAPI managed
 * configuration, a one-time claim token exchanged for a credential) is the
 * device-fleet track; until then this is an explicit placeholder rather than a
 * silent one.
 */
const shift = new ShiftSession({
  deviceSecret: config.deviceSecret,
  /**
   * The one online step: a PIN is exchanged for a device-bound verifier, once
   * per person per device. Everything afterwards is verified locally.
   */
  enrol: (staffId, pin) =>
    enrolOnDevice({
      baseUrl: config.baseUrl,
      apiKey: config.apiKey,
      venueId: config.venueId,
      deviceId: config.deviceId,
      staffId,
      pin,
    }),
});

/**
 * WHAT THIS DEVICE HAS ALREADY MINTED, BACK OFF THE DISK.
 *
 * `installCredentials` existed from the day enrolment was written and had NO
 * CALLER, so the bundle lived in memory only: every reload — a refresh, a
 * tablet waking, a crash mid-service — threw it away and sent the next sign-in
 * back to the network. On a till whose entire premise is that a PIN is verified
 * locally, that is the one thing it must not need.
 *
 * Restored BEFORE the roster is fetched, so a till that starts with no network
 * can still sign in anybody already enrolled on it.
 */
async function restoreCredentials(): Promise<void> {
  const held = await storage.credentials?.get(new Date());
  if (held) {
    shift.installCredentials(held);
    // See `shiftVersion`: the session is a plain object and Vue has no reason
    // to re-render for it.
    shiftVersion.value += 1;
  }
}

/**
 * The floor plan and the menu, off the device, BEFORE any network call.
 *
 * A till reloaded during an outage used to boot, let somebody sign in, and then
 * show them nothing to ring: no tables, no items. Every warning in the log said
 * "keeping the cached copy" and there was no cached copy — the cache was a
 * variable in a module the reload had just discarded.
 *
 * The config goes back through `accept`, which VERIFIES ITS SIGNATURE exactly
 * as it does for a bundle off the network. A bundle edited on disk verifies
 * against nothing and is refused here, loudly, rather than trusted for being
 * local.
 */
async function restoreOfflineCache(): Promise<void> {
  const cache = storage.cache;
  if (!cache) return;

  try {
    const held = await cache.get<Parameters<typeof readVenueConfig>[0]>("config");
    if (held) {
      configCache ??= await createConfigCache({
        publicKeyRaw: config.configPublicKey,
        tenantId: config.tenantId,
        venueId: config.venueId,
      });
      const result = await configCache.accept(held.payload as never, new Date());
      if (!result.accepted) console.warn(`config: refused the stored bundle — ${result.reason}`);
      const bundle = configCache.current();
      if (bundle) state.venue = readVenueConfig(bundle.payload);
    }
  } catch (error) {
    console.warn("config: could not restore the stored bundle —", (error as Error).message);
  }

  try {
    const held = await cache.get<MenuSnapshot>("menu");
    if (held) {
      // A copy cached before categories and modifiers existed lacks them.
      const menu = normalizeMenu(held.payload);
      book.offerMenu(menu);
      menuState.current = menu;
    }
  } catch (error) {
    console.warn("menu: could not restore the stored copy —", (error as Error).message);
  }
}

/** And back to the disk, after every successful online enrolment. */
async function persistCredentials(): Promise<void> {
  const held = shift.credentials();
  if (held) await storage.credentials?.put(held);
}

/**
 * A counter that exists only to be depended on.
 *
 * `ShiftSession` holds the roster and the credential bundle and is not
 * reactive. Anything that changes it has to say so, or the screen reading
 * `shift.candidates()` never re-renders.
 */
const shiftVersion = ref(0);

const login = reactive<{ error: string | null; lockedForSeconds: number; busy: boolean }>({
  error: null,
  lockedForSeconds: 0,
  busy: false,
});

/**
 * Fetch the roster — names and roles for the picker.
 *
 * No verifiers and no PINs travel here. A failure keeps the cached names, so
 * the login screen still works during an outage for anybody already enrolled
 * on this device.
 */
/**
 * JOIN THE DRAWER, IF ONE IS ALREADY OPEN ON IT.
 *
 * The till opened a NEW shift every time somebody counted in, so a venue with
 * two tills on one drawer — the SHARED_TILL model almost every Czech pub runs
 * — had two open shifts on one box of money. Each Z-report then balanced
 * against half the cash, and the server, which refuses to guess which of two
 * open drawers a tender belongs to, put that cash on NEITHER report: it showed
 * up under "hotovost bez otevřené pokladny" and balanced against nothing.
 *
 * Runs AFTER the local journal restore, so a till that has its own open shift
 * keeps it — `adopt` refuses over the top of one, because those events are
 * unsent work and a float somebody counted by hand.
 *
 * Silent when there is nothing to join, which is the ordinary case: the first
 * till of the day counts in and opens the drawer as it always did.
 */
async function adoptOpenShift(): Promise<void> {
  // Lazy: this runs once, after first paint, and never again. It also finds a
  // shift left open from an earlier day, for count-in to close first.
  const started = await (await import("./shift-join")).startDrawer(drawer, state.venue, config);
  drawerState.stale = started.stale;
  if (started.joined) drawerState.version += 1;
}

async function syncShift(): Promise<void> {
  try {
    shift.installRoster(
      await fetchRoster({
        baseUrl: config.baseUrl,
        apiKey: config.apiKey,
        venueId: config.venueId,
        deviceId: config.deviceId,
      }),
    );
    // What the roster pruned is gone from storage too, or a reload with no
    // network would bring the old credentials back.
    await persistCredentials();
  } catch (error) {
    console.warn("shift: keeping the cached roster —", (error as Error).message);
  }
  shiftVersion.value += 1;
}

/**
 * The guest code for the check currently on screen.
 *
 * Kept in reactive state rather than computed, because minting involves an
 * HMAC and is therefore asynchronous, and a render cannot await. It is cheap —
 * one HMAC per check — and it is re-minted on every check change so a code
 * never outlives the bill it names.
 */
const guestState = reactive<{ url: string | null; forCheckId: string | null }>({
  url: null,
  forCheckId: null,
});

/** Where a guest's phone should go. Deployment configuration, like the API. */
const guestBaseUrl = import.meta.env["VITE_GF_GUEST"] ?? "http://localhost:5176";

async function refreshGuestUrl(): Promise<void> {
  const view = state.check;
  const key = state.venue?.guestTokenKey ?? null;
  const orderId = view.orderId;
  const checkId = view.checkId;

  if (!key || !orderId || !checkId) {
    guestState.url = null;
    guestState.forCheckId = null;
    return;
  }
  if (guestState.forCheckId === checkId && guestState.url !== null) return;

  const token = await mintGuestToken({
    venueId: config.venueId,
    orderId,
    checkId,
    secret: key,
    now: new Date(),
    // A fresh nonce per mint, so a reprint is a different code and a guest who
    // kept the old one does not hold a working key to a reopened bill. Four
    // bytes is what a QR can afford; it is uniqueness, not entropy — the MAC is
    // what makes a code unforgeable.
    nonce: crypto.getRandomValues(new Uint32Array(1))[0]!,
  });

  guestState.forCheckId = checkId;
  /**
   * The token rides in the FRAGMENT, not the query string.
   *
   * A fragment is not sent to the server on the document request, does not
   * appear in an access log, and does not travel in a `Referer`. It is a bearer
   * credential for one bill; keeping it out of every log between the phone and
   * the origin is free.
   */
  guestState.url = `${guestBaseUrl}/#t=${encodeURIComponent(token)}`;
}

let hlcSeq = 0;

/**
 * The drawer, sitting BETWEEN the sessions and the outbox.
 *
 * Every event the till emits passes through it, which is how the Z-report's
 * cash total is derived from the same `tender.recorded` events the server will
 * fold rather than counted in parallel. A second accumulator agrees on every
 * ordinary evening and disagrees on exactly the one somebody asks about.
 *
 * The business date is derived here from the venue clock in the SIGNED config
 * bundle, using the same `businessDateOf` the server uses — so the device and
 * the server agree by construction. Before the first bundle lands there is no
 * clock and no shift can be opened; that is correct, because a shift opened
 * against a guessed business day is filed on a date no report looks at.
 */
const drawer = new DrawerSession({
  outbox,
  // Absent when OPFS is unavailable. A till that refused to open a drawer
  // because it could not persist one would be a venue that cannot trade, so it
  // degrades to an in-memory shift rather than to no shift.
  ...(storage.journal ? { journal: storage.journal } : {}),
  now: () => new Date(),
  newId: () => crypto.randomUUID(),
  nextHlc: () => `${String(++hlcSeq).padStart(15, "0")}:000000:${config.deviceId}`,
  businessDate: () => {
    const clock = state.venue?.clock;
    if (!clock) throw new Error("no venue clock yet");
    return businessDateOf(new Date(), clock);
  },
  deviceId: config.deviceId,
});

/**
 * A handle for the browser test, and for a support session on a real tablet.
 *
 * READS ONLY THINGS ALREADY DECIDED ELSEWHERE — it exposes the composed outbox
 * rather than a way to reach around it. The alternative for testing durability
 * was a page script that opened its own database, which would prove OPFS works
 * and say nothing about whether the POS uses it.
 *
 * `durable` and `configured` are the two facts a screenshot cannot show: an
 * in-memory fallback behaves identically until the reload that loses the
 * evening, and a till with no verified config bundle looks fine until somebody
 * tries to count a drawer in.
 */
(globalThis as unknown as { __gf: unknown }).__gf = {
  outbox,
  drawer,
  get durable() {
    return storage.durable;
  },
  /** True once a SIGNED bundle has verified and been installed. */
  get configured() {
    return state.venue !== null;
  },
};

/**
 * Receipt numbers, leased from the server and spent offline.
 *
 * ABSENT WHEN STORAGE IS, and that is the honest shape. Without OPFS there is
 * nowhere to persist the cursor, so the rule that makes the whole scheme safe —
 * persist BEFORE emitting — cannot be kept. A till in that state already tells
 * `sellState` it cannot store sales; issuing numbers it might reuse after a
 * reload would be strictly worse than refusing to close a check.
 */
const receiptNumbers = storage.leases
  ? new ReceiptNumbers({
      store: storage.leases,
      source: new NumberingClient({
        baseUrl: config.baseUrl,
        apiKey: config.apiKey,
        venueId: config.venueId,
        deviceId: config.deviceId,
      }),
    })
  : undefined;

const drawerState = reactive<{
  error: string | null;
  busy: boolean;
  version: number;
  /** A shift left open on this drawer from an earlier day, to close first. */
  stale: StaleShift | null;
}>({
  error: null,
  busy: false,
  stale: null,
  // Bumped after every drawer command so the computed props re-read the fold.
  // The fold is the authority; this only tells Vue that it moved.
  version: 0,
});

const applyDrawer = async (result: { ok: boolean; messageKey?: string }): Promise<void> => {
  drawerState.error = result.ok ? null : (result.messageKey ?? "pos.error.unknown");
  drawerState.version += 1;
  await refresh();
};

/**
 * The book of open checks, one session per table.
 *
 * The till used to hold exactly ONE session, opened implicitly against no table
 * at all, so a server could not seat a second party without settling the first.
 * Every session shares this outbox, because the blocking FIFO is per DEVICE:
 * a void on table 4 must never overtake the sale it reverses because table 7
 * happened to be quiet.
 */
/**
 * Render the receipt for a check that has just settled.
 *
 * LAZY, and by the rule the bundle budget now carries: nobody looks at a
 * receipt before first paint, and this pulls in the whole printing module.
 * The service worker precaches the chunk, so it works during an outage — which
 * is the only time the printed copy is the one a guest gets.
 */
async function showReceipt(check: CheckView): Promise<void> {
  const clock = state.venue?.clock;
  const checkId = check.checkId;
  state.receiptPrint = null;

  try {
    const { receiptStateFor } = await import("./receipt");
    state.receipt = receiptStateFor({
      check,
      seller: state.venue?.seller ?? null,
      receiptNumber: checkId ? (receiptNumbersByCheck.get(checkId) ?? null) : null,
      closedAt: new Date(),
      // The VENUE's zone, never the device's and never a literal: a receipt
      // dated in the wrong zone can put a sale in the wrong VAT period, and it
      // does it silently. Null refuses rather than guesses.
      timeZone: clock?.timezone ?? null,
      // §30a's threshold, from the signed bundle and originally from the
      // regulatory registry. Null refuses rather than assumes a Czech one.
      simplifiedLimitMinor: state.venue?.simplifiedLimitMinor ?? null,
      locale: localeRef.value,
      currency: "CZK",
    });
  } catch (error) {
    console.warn("receipt: could not render —", (error as Error).message);
    state.receipt = { lines: null, refusalKey: "pos.receipt.refused.FAILED", warningKeys: [] };
  }
}

/**
 * The numbers this till has issued, by check.
 *
 * Small and unbounded in principle; in practice one entry per check closed
 * since the page loaded, which a reload clears. It exists to answer "what was
 * the number on the receipt I just handed over", and nothing else reads it.
 */
const receiptNumbersByCheck = new Map<string, string>();

/**
 * What the kitchen has run out of.
 *
 * A module-level set rather than state on a session, because sessions are
 * long-lived and availability changes mid-service: a check opened at seven must
 * refuse a dish that ran out at eight, and re-creating every open session to
 * tell it so would throw away the floor. Sessions read it through a getter.
 *
 * STARTS EMPTY, which means "nothing is off" rather than "unknown". That is the
 * direction that keeps a venue selling: a till that refused everything because
 * one fetch failed would be worse than one that briefly offers a dish the
 * kitchen has run out of, which a server finds out by walking to the pass.
 */
const soldOut = reactive(new Set<string>());

/**
 * One session, however it is reached — a table on the floor or a receipt read
 * back to be taken back. Every one emits through the drawer and the journal.
 */
const newSession: SessionFactory = ({ menu: m }) =>
  new PosSession({
    // The drawer, not the outbox. It observes tenders on their way past and
    // forwards every event unchanged.
    outbox: {
      /**
       * Two writes, and the JOURNAL GOES FIRST.
       *
       * `drawer.observe` forwards to the outbox, which is delivery. The journal
       * is this device's memory of what is on its own floor. A crash between
       * the two leaves an event journalled but not queued — recoverable, since
       * the server has not seen it and the till can still show the check. The
       * other order leaves a sale on its way to the server that this till has
       * forgotten, which is the table that disappears.
       */
      enqueue: async (event) => {
        /**
         * THE RECEIPT NUMBER IS CAUGHT HERE, and it has to be.
         *
         * `foldOrder` deliberately ignores `receipt.issued` — "folding it into
         * order state would create a second copy of a fact the log already
         * holds" — so the check view does not carry the number and should not.
         * This hook already sees every event the session emits, which is where
         * the drawer takes its tenders from for the same reason.
         */
        if (event.type === "receipt.issued") {
          const p = event.payload as { checkId?: unknown; receiptNumber?: unknown };
          if (typeof p.checkId === "string" && typeof p.receiptNumber === "string") {
            receiptNumbersByCheck.set(p.checkId, p.receiptNumber);
          }
        }
        await storage.orders?.append(event);
        await drawer.observe(event);
      },
    },
    menu: m,
    // Read at EMIT time, not at construction: a session outlives a sign-in, and
    // capturing the actor once would attribute a whole evening to whoever
    // happened to open the first check.
    get actorId() {
      return actorId();
    },
    deviceId: config.deviceId,
    now: () => new Date(),
    nextHlc: () => `${String(++hlcSeq).padStart(15, "0")}:000000:${config.deviceId}`,
    // Spread rather than assigned: without OPFS there is no lease store, and
    // `exactOptionalPropertyTypes` is right to insist that "absent" and
    // "present but undefined" are different things here.
    ...(receiptNumbers ? { receiptNumbers } : {}),
    newId: () => crypto.randomUUID(),
    /**
     * A GETTER, read at ring time.
     *
     * Sessions outlive the answer: a check opened at seven has to refuse a dish
     * the kitchen ran out of at eight, and passing a snapshot would freeze
     * availability at whatever it was when the table was seated.
     */
    unavailable: () => soldOut,
  });

const book = new CheckBook({ version: 0, resolvedAt: new Date(0), categories: [], items: [] }, newSession);

// --- Today's receipts ----------------------------------------------------------

/**
 * The receipts screen: today's closed checks, reprinted or taken back. The
 * state lives here; the work in `./receipts-controller`, loaded on first use —
 * nobody opens the receipts before first paint.
 */
const receiptBook = shallowRef<ReceiptBook | null>(null);
let receiptsLoaded: Promise<ReceiptsController> | null = null;
const receipts = (): Promise<ReceiptsController> =>
  (receiptsLoaded ??= import("./receipts-controller").then(({ receiptsController }) =>
    receiptsController({
      book: receiptBook,
      newSession,
      menu: () => menuState.current,
      venue: () => state.venue,
      wire: { baseUrl: config.baseUrl, apiKey: config.apiKey, venueId: config.venueId },
      locale: () => localeRef.value,
      t: () => translatorRef.value,
      approve: (action, approval) => approve(action, approval),
      fail: (key) => {
        state.error = { key };
      },
      refresh,
    }),
  ));

/**
 * The receipt the till would have printed.
 *
 * `lines: null` while the chunk loads; a `refusalKey` when there is no honest
 * document to show. Held on the screen state rather than in the dialog, because
 * the dialog is lazily loaded and the answer is computed before it arrives.
 */
interface ReceiptState {
  lines: readonly string[] | null;
  refusalKey: string | null;
  warningKeys: readonly string[];
  /** What *Tisk* sends, and its number. A type import: the module itself stays in the lazy chunk. */
  doc?: import("@gf/module-printing").PrintDoc | null;
  label?: string | null;
}

interface ScreenState {
  receipt: ReceiptState | null;
  /** Where *Tisk* got with the receipt on screen: sent to the printer, or not. */
  receiptPrint: "sending" | "sent" | "failed" | null;
  /** What the kitchen has finished, and what it is still making. */
  ready: ReadyEntry[];
  /** Today's bookings, when the venue takes them. */
  bookings: FloorBooking[];
  /** The number just paid for at the counter, shown with its receipt. */
  pickupShown: string | null;
  check: CheckView;
  queuedCount: number;
  online: boolean;
  /**
   * The last refusal, as a message key.
   *
   * Every PosSession method returns a PosResult carrying a translatable
   * messageKey, and NOTHING READ IT: `.then(refresh)` discarded the failure, so
   * a refused tender, an unknown item or an unbalanced close produced exactly
   * nothing on screen. A till that silently ignores a tap teaches staff it is
   * broken, and they stop trusting the ones that do work.
   */
  error: { key: string; params?: Readonly<Record<string, string>> } | null;
  /** The table being served, or null while the floor is showing. */
  activeTable: string | null;
  /** The venue's configuration, once a signed bundle has been verified. */
  venue: VenueConfig | null;
  /**
   * An age check the session is waiting on, or null.
   *
   * NOT an `error`. A refusal goes in the banner and is something that went
   * wrong; this is a step in serving somebody, and putting it in the same field
   * would make a legal control look like a fault.
   */
  ageCheck: { items: string; selectionIds: readonly string[] } | null;
  ageBusy: boolean;
  /** A buyer lookup is in flight. */
  buyerBusy: boolean;
  /** What the register said, already translated. Null when nothing went wrong. */
  buyerNotice: string | null;
  /**
   * What a successful lookup returned, for the dialog to show BEFORE it is
   * printed. A server has to be able to see they looked up the right company;
   * one that silently accepted the answer would put another company's name on a
   * legal document and look entirely correct doing it.
   */
  buyerFound: {
    legalName: string;
    registrationId: string;
    vatId: string | null;
    address: string | null;
  } | null;
}

const EMPTY_CHECK: CheckView = {
  orderId: null,
  checkId: null,
  tableRef: null,
  consumptionMode: "DINE_IN",
  state: "EMPTY",
  lines: [],
  totals: {
    grossMinor: 0n,
    discountMinor: 0n,
    compMinor: 0n,
    dueMinor: 0n,
    vatMinor: 0n,
    roundingMinor: 0n,
    tipMinor: 0n,
    outstandingMinor: 0n,
    taxLines: [],
  },
  buyer: null,
  menuVersion: 0,
  unfiredCount: 0,
  version: 0,
  tenders: [],
};

const state = reactive<ScreenState>({
  check: EMPTY_CHECK,
  queuedCount: 0,
  online: navigator.onLine,
  error: null,
  activeTable: null,
  venue: null,
  ageCheck: null,
  ageBusy: false,
  buyerBusy: false,
  buyerNotice: null,
  buyerFound: null,
  receipt: null,
  receiptPrint: null,
  ready: [],
  bookings: [],
  pickupShown: null,
});

/** This till's counter letter, when the venue runs a counter and this till is at it. */
const counterPrefix = (): string | null =>
  state.venue?.modules.includes("counter") ? (state.venue.counters[config.deviceId] ?? null) : null;

/**
 * A counter till's numbers, saved before one is handed out like receipt
 * numbers — on the same durable store. Loaded on the first counter sale: a
 * till at the tables never needs it.
 */
let pickupNumbers: Promise<PickupNumbers> | null = null;
const pickups = (): Promise<PickupNumbers> =>
  (pickupNumbers ??= (async () => {
    const { MemoryPickupCounter, PickupNumbers, SqlitePickupCounter } = await import("./pickup-numbers");
    const durable = storage.driver ? new SqlitePickupCounter(storage.driver) : null;
    await durable?.migrate();
    return new PickupNumbers({ counter: durable ?? new MemoryPickupCounter(), clock: () => state.venue?.clock ?? null });
  })());

/** Handed over at the counter or carried out to the table: off the kitchen's list. */
function handOver(orderId: string): void {
  void (async () => {
    const entry = state.ready.find((r) => r.orderId === orderId);
    if (!entry) return;
    state.ready = state.ready.filter((r) => r.orderId !== orderId);
    for (const selectionId of entry.selectionIds) {
      await outbox.enqueue({
        eventId: crypto.randomUUID(),
        streamId: orderId,
        type: "selection.progressed",
        payload: { selectionId, to: "DELIVERED" },
        hlc: `${String(++hlcSeq).padStart(15, "0")}:000000:${config.deviceId}`,
        occurredAt: new Date(),
        actorId: actorId(),
      });
    }
    await refresh();
  })();
}

/**
 * What the kitchen has finished. Read when the venue's stream says a ticket
 * moved, and every twenty seconds beneath that. A chime for what has JUST
 * turned ready — the counter's numbers on a counter till, tables on a waiter's.
 */
async function syncReady(): Promise<void> {
  if (!state.venue?.modules.includes("kitchen-display")) {
    state.ready = [];
    return;
  }
  try {
    const { chime, fetchReady, newlyReady } = await import("./service-ready");
    const next = await fetchReady({ baseUrl: config.baseUrl, apiKey: config.apiKey, venueId: config.venueId });
    const fresh = newlyReady(state.ready, next);
    const mine = counterPrefix() ? fresh.some((e) => e.pickupCode) : fresh.some((e) => !e.pickupCode && e.tableRef);
    state.ready = next;
    if (mine && signedIn.staffId) chime();
  } catch (error) {
    console.warn("ready: keeping what is shown —", (error as Error).message);
  }
}

/**
 * Today's bookings, for the floor's "who is coming". Plain rows, not events,
 * so nothing on the venue's stream says they moved: read every minute, and
 * kept as shown when the server cannot be reached.
 */
async function syncBookings(): Promise<void> {
  if (!state.venue?.modules.includes("reservations")) {
    state.bookings = [];
    return;
  }
  try {
    const { fetchBookings } = await import("./bookings");
    state.bookings = await fetchBookings({ baseUrl: config.baseUrl, apiKey: config.apiKey, venueId: config.venueId });
  } catch (error) {
    console.warn("bookings: keeping what is shown —", (error as Error).message);
  }
}

/**
 * *Tisk*: to the receipt printer the office set up, or — with none, or no
 * network — the browser's own dialog. A laptop demonstration has a PDF printer
 * and a small venue may genuinely have one; a `@media print` block in the
 * dialog narrows the page to the paper.
 */
async function printReceipt(): Promise<void> {
  const printer = state.venue?.receiptPrinter ?? null;
  const receipt = state.receipt;
  if (!printer || !receipt?.doc || !receipt.label) {
    window.print();
    return;
  }
  state.receiptPrint = "sending";
  const { sendReceipt } = await import("./print-receipt");
  const outcome = await sendReceipt({ baseUrl: config.baseUrl, apiKey: config.apiKey, venueId: config.venueId, label: receipt.label, doc: receipt.doc });
  state.receiptPrint = outcome === "sent" ? "sent" : "failed";
  if (outcome !== "sent") window.print();
}

/** The session for the table being served, or undefined on the floor. */
const active = (): PosSession | undefined =>
  state.activeTable === null ? undefined : book.get(state.activeTable);

/**
 * The book moved. It is plain TypeScript, not reactive, so the floor read from
 * it re-renders on this — without it a floor restored offline, where nothing
 * else changes, stayed "0 z 11 obsazeno" over four tables of unpaid bills.
 */
const bookVersion = ref(0);

const refresh = async (): Promise<void> => {
  bookVersion.value++;
  state.check = active()?.view() ?? EMPTY_CHECK;
  state.queuedCount = await outbox.depth();
  // After the check, so the code is minted for the check now on screen.
  await refreshGuestUrl();
};

/**
 * Apply a session result: refresh on success, surface the reason on failure.
 *
 * One function, so no call site can forget. The previous shape — `.then(refresh)`
 * at every call — made forgetting the default.
 */
/**
 * Fire, and stop for an age check if one is owed.
 *
 * The REFUSAL is what drives the dialog — `fire()` returns AGE_CHECK_REQUIRED
 * with the lines named, and this turns that into a prompt. Deliberately not the
 * other way round: a screen that decided for itself which lines need checking
 * would be a second copy of the rule, and the copy that drifts is the one
 * nobody is looking at. The session is the authority and this reacts to it.
 */
async function send(): Promise<void> {
  const session = active();
  if (!session) return;

  const result = await session.fire();
  if (result.ok || result.code !== "AGE_CHECK_REQUIRED") {
    await applyResult(result);
    return;
  }

  // Held, not failed: the dialog is the next step, not an error to report.
  state.ageCheck = {
    items: result.params?.["items"] ?? "",
    selectionIds: (result.params?.["selectionIds"] ?? "").split(",").filter(Boolean),
  };
  await refresh();
}

async function confirmAge(): Promise<void> {
  const pending = state.ageCheck;
  const session = active();
  if (!pending || !session) return;

  state.ageBusy = true;
  try {
    await session.verifyAge(pending.selectionIds);
    state.ageCheck = null;
    // Straight on to the fire the server was trying to do. Making somebody
    // press Send twice for one act is how a prompt becomes a thing to resent.
    await applyResult(await session.fire());
  } finally {
    state.ageBusy = false;
  }
}

const drawerIsOpen = (): boolean => drawer.state() !== undefined && drawer.state()?.closedAt === null;

const applyResult = async (result: { ok: boolean; messageKey?: string; params?: Readonly<Record<string, string>> }): Promise<void> => {
  state.error = result.ok
    ? null
    : { key: result.messageKey ?? "pos.error.unknown", ...(result.params ? { params: result.params } : {}) };
  await refresh();
};

for (const event of ["online", "offline"] as const) {
  window.addEventListener(event, () => {
    state.online = navigator.onLine;
  });
}

/**
 * The three-state banner.
 *
 * Recomputed from live condition rather than stored, so "can this till sell?"
 * has exactly one answer and it is the one `sellState` gives.
 */
const condition = () => ({
  connected: state.online,
  outboxDepth: state.queuedCount,
  // REAL, at last. This was `Number.MAX_SAFE_INTEGER`, which told `sellState`
  // the till could always sell — so NO_RECEIPT_NUMBERS, the one blocking
  // reason that actually stops a restaurant, was unreachable. Zero without a
  // lease store is the honest answer: a till that cannot persist a cursor
  // cannot safely issue a number.
  numbersRemaining: receiptNumbers?.remaining() ?? 0,
  entitlementValidUntil: null,
  // From the VERIFIED config bundle, not a placeholder. These were hardcoded
  // null, so the till could never report CONFIG_STALE or CONFIG_EXPIRED however
  // long its configuration had been unreachable.
  configExpiresAt: configCache?.posture(new Date()).expiresAt ?? null,
  configGraceUntil: configCache?.posture(new Date()).graceUntil ?? null,
  now: Date.now(),
  appVersion: 1,
  minOperatingVersion: 1,
});

// --- Updates ---------------------------------------------------------------

/**
 * The service worker installs and then WAITS. It is told to activate only when
 * the gate says the venue is at a safe moment — never on its own, because
 * `skipWaiting()` on a till reloads a server's half-built check.
 */
const updateGate = new UpdateGate({
  idleThresholdMs: 120_000,
  // Aligned with the Android Management API system-update window: one
  // maintenance window per venue, not two that fight.
  quietHours: [{ fromHour: 3, toHour: 5 }],
});

let lastInteractionAt = Date.now();

for (const event of ["pointerdown", "keydown"] as const) {
  window.addEventListener(event, () => {
    lastInteractionAt = Date.now();
    if (signedIn.staffId) touchSignIn(deviceStorage, lastInteractionAt);
  });
}

/**
 * Signing out LEAVES EVERY CHECK OPEN: a shift change settles nobody's bill,
 * and the next server picks up the same tables.
 */
function signOut(): void {
  signedIn.staffId = null;
  signedIn.displayName = null;
  signedIn.roleId = null;
  state.activeTable = null;
  login.error = null;
  releaseSignIn(deviceStorage);
}

// A till left alone locks, so the next person signs in as themselves.
window.setInterval(() => {
  if (signedIn.staffId && shouldLock(lastInteractionAt, Date.now())) signOut();
}, 30_000);

/** Asked while a new build waits. */
const mayUpdate = (): boolean =>
  updateGate.evaluate({
    hasStagedUpdate: true,
    openCheckCount: state.check.state === "OPEN" ? 1 : 0,
    outboxDepth: state.queuedCount,
    shiftOpen: true,
    idleMs: Date.now() - lastInteractionAt,
    now: Date.now(),
    managerRequested: false,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).apply;

async function registerServiceWorker(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;

  /**
   * NOT IN DEVELOPMENT, because there is nothing to register.
   *
   * `vite dev` does not build `src/sw.ts`, so `/sw.js` falls through to the
   * HTML fallback and the browser refuses it: "The script has an unsupported
   * MIME type ('text/html')". Harmless, and red in the devtools of every
   * developer and every demo — which is exactly the kind of noise that teaches
   * people to ignore the console, and then to miss the line that matters.
   *
   * The built artifact still registers it, and the browser suite still proves
   * the offline shell works against a real build.
   */
  if (import.meta.env["DEV"]) return;

  /**
   * A registration failure is not fatal, and must not be silent either.
   *
   * `vite dev` serves no /sw.js, so in development this rejected on every load
   * and the unhandled rejection sat in devtools looking like a real fault —
   * which is exactly the red text somebody notices over your shoulder during a
   * demo. In a built artifact the same rejection means something serious: the
   * offline shell is gone, so the till still sells but will not COLD-START
   * without a network. One case is expected and one is a defect, the only
   * difference is whether the file was built, so name what happened rather than
   * swallowing both.
   */
  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register("/sw.js", { type: "module" });
  } catch (error) {
    console.warn(
      "pos: no service worker — this build cannot cold-start offline.",
      error instanceof Error ? error.message : error,
    );
    return;
  }

  // A new build is looked for, waits for the gate, and the page reloads into it.
  const { followAppUpdates } = await import("@gf/sync-client/app-update");
  followAppUpdates({
    container: navigator.serviceWorker,
    registration,
    mayApply: mayUpdate,
    reload: () => location.reload(),
  });
}

// --- Mount -----------------------------------------------------------------

applyTheme(preferredTheme(), "pos" satisfies DensityName);
window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
  applyTheme(preferredTheme(), "pos");
});

/**
 * MOUNTED THROUGH A RENDER FUNCTION, and the reason is two bugs in one.
 *
 * The props used to be an object literal of GETTERS handed straight to
 * `createApp`. Vue evaluates root props ONCE, eagerly, outside any render
 * effect — so:
 *
 *   NOTHING EVER UPDATED. `state.check` changed when a line was rung and the
 *   screen kept the value it had been handed at mount. A till that does not
 *   redraw when you ring an item is not a till.
 *
 *   AND THE TILL DID NOT START AT ALL. One of those getters calls
 *   `condition()`, which reads `configCache` — a `let` declared two hundred
 *   lines further down. Evaluating it at mount hit the temporal dead zone and
 *   threw "Cannot access 'configCache' before initialization", killing the
 *   module before a single element was drawn. A blank screen, in a real
 *   browser, on every load.
 *
 * Every unit test passed, because component tests mount components with plain
 * props. The bundle gate passed, because the artifact builds perfectly. Only
 * opening the page in Chrome says otherwise — which nothing in CI does.
 *
 * Evaluating the props INSIDE a render function fixes both at once: the reads
 * happen lazily, after the module has finished initialising, and inside an
 * effect that tracks them.
 */
/**
 * Close the check at the open table: the document, the receipt, the floor.
 * Shared by "Uzavřít účet" and the one-tap "take it and close".
 */
/** Close the check being served — or, with `closed`, carry on after a close already made (a counter sale). */
async function closeActiveCheck(closed?: { ok: true }): Promise<void> {
  const session = active();
  if (!session) return;

  const result = closed ?? (await session.closeCheck());
  if (!closed) await applyResult(result as Parameters<typeof applyResult>[0]);

  /**
   * ONE GUEST OF A SPLIT TABLE HAS PAID, and the others have not.
   *
   * `retire` below refuses — the order is still trading — so this used to do
   * nothing at all: no receipt for the guest who had just paid, and the screen
   * left on their closed check still offering Zaplatit. The receipt is theirs
   * now, at the moment it would print, and the screen moves on to the next
   * guest who owes something.
   */
  if (result.ok && session.view().closed === true && session.view().state === "OPEN") {
    state.receipt = { lines: null, refusalKey: null, warningKeys: [] };
    void showReceipt(session.view());
    const next = session.checks().find((c) => !c.closed && !c.vacant);
    if (next) await applyResult(session.selectCheck(next.checkId));
    return;
  }

  // Settled, so the table is free and the server goes back to the floor —
  // which is where they were heading anyway. `retire` refuses an unsettled
  // check, so a refused close leaves them exactly where they are.
  if (result.ok && state.activeTable !== null && book.retire(state.activeTable)) {
    /**
     * AFTER the close was appended, never before.
     *
     * `applyResult` has already run, so the close event is in the journal
     * and in the outbox. Pruning now forgets an order the till no longer
     * has to show; pruning any earlier would risk a crash leaving a paid
     * table sitting open on the floor plan.
     *
     * The outbox still owns DELIVERY. A close that has not reached the
     * server yet is still queued, and nothing here touches that.
     */
    const orderId = session.view().orderId;
    if (orderId !== null) await storage.orders?.forget(orderId);

    /**
     * THE RECEIPT, AT THE MOMENT A SERVER WOULD TEAR IT OFF.
     *
     * A real till prints on close and the server hands over paper. No
     * printer is wired, so the dialog IS the paper: it appears at exactly
     * the moment the paper would, and one tap replaces the tear.
     *
     * Opened BEFORE the await below, with `lines: null`, so the screen is
     * never blank while a chunk loads — the dialog says it is working.
     */
    state.receipt = { lines: null, refusalKey: null, warningKeys: [] };
    void showReceipt(session.view());

    state.activeTable = null;
    await refresh();
  }
}

/**
 * Who authorised an act: the person signed in, when they may; otherwise the
 * manager whose PIN was given — checked by the shift offline, with the same
 * lockout as sign-in. Null, with the refusal on screen, when nobody valid did.
 */
async function approve(
  action: ApprovalAction,
  approval: { staffId: string; pin: string } | undefined,
): Promise<string | null> {
  if (!needsApproval(action, signedIn.roleId)) return actorId();
  if (!approval) {
    state.error = { key: "pos.approval.required" };
    return null;
  }
  const outcome = await shift.signIn(approval.staffId, approval.pin, new Date());
  if (!outcome.ok || !canApprove(outcome.roleId, action)) {
    state.error = { key: "pos.approval.refused" };
    return null;
  }
  return outcome.staffId ?? approval.staffId;
}

/** For the minutes a table has been open; a tick, not a timer per tile. */
const clock = ref(new Date());
window.setInterval(() => (clock.value = new Date()), 30_000);

const screenProps = () => {
  /**
   * Read through `version` so Vue re-evaluates after a drawer command.
   *
   * The fold inside the `DrawerSession` is the authority and is not reactive —
   * it is plain TypeScript, deliberately, so it can be tested without a DOM.
   * This counter is the signal that it moved, and reading it here makes the
   * whole props object depend on it exactly once rather than three times.
   */
  void drawerState.version;
  void bookVersion.value;

  return {
  // Getters rather than snapshots, so the reactive state drives the props
  // without the screen needing to know where it came from.
  check: state.check,
  menu: menuState.current,
  /**
   * Read inside `props()`, which runs per render.
   *
   * Empty when no table is open, which is what the strip expects — it hides
   * itself below two checks and an empty list is simply the floor plan being
   * on screen instead.
   */
  checks: active()?.checks() ?? [],
  // Read inside `props()`, which runs per render, so a dish going off reaches
  // the grid without the screen being rebuilt.
  soldOut: soldOut as ReadonlySet<string>,
  // Read through the ref, so switching language re-renders. A captured value
  // here would be frozen at mount.
  t: translatorRef.value,
  simplifiedLimitMinor: state.venue?.simplifiedLimitMinor ?? null,
  buyerBusy: state.buyerBusy,
  buyerNotice: state.buyerNotice,
  buyerFound: state.buyerFound,
  locale: localeRef.value,
  locales: AVAILABLE_LOCALES,
  receipt: state.receipt,
  receiptPrinter: state.venue?.receiptPrinter?.name ?? null,
  receiptPrint: state.receiptPrint,
  pickupShown: state.pickupShown,
  courses: state.venue?.modules.includes("courses") === true,
  counter: counterPrefix()
    ? {
        prefix: counterPrefix()!,
        openSales: book.counterSales(),
        ready: state.ready.filter((r) => r.pickupCode !== null),
        tables: state.venue?.modules.includes("tables") === true,
      }
    : null,
  readyTables: state.ready.filter((r) => r.state === "READY" && r.pickupCode === null && r.tableRef).map((r) => r.tableRef!),
  bookings: state.bookings,
  receiptBook: receiptBook.value,
  currency: "CZK",
  ageCheck: state.ageCheck,
  ageBusy: state.ageBusy,
  sellState: sellState(condition()).state,
  reasons: sellState(condition()).reasons,
  queuedCount: state.queuedCount,

  error: state.error,
  // Computed every render from the open sessions, never a stored flag. A
  // second source of truth about which tables owe money disagrees with the
  // first the moment a check settles mid-render.
  floor: book.floor(state.venue?.floor ?? []),
  tabs: book.tabs(
    state.venue?.floor ?? [],
    (n) => translatorRef.value.t("pos.floor.tabName", { n }),
    (table) => translatorRef.value.t("pos.floor.apart", { table }),
    state.venue?.tableNames,
  ),
  now: clock.value,
  staffNames: Object.fromEntries(
    (shiftVersion.value, shift.candidates()).map((c) => [c.staffId, c.displayName]),
  ),
  activeTable: state.activeTable,
  /**
   * From the VERIFIED config bundle, where it has always been parsed and never
   * been read. Czech rounding worked by the COINCIDENCE of the default being
   * 100 — which is right for CZK and would be silently wrong the first time
   * this ran anywhere else.
   */
  cashIncrementMinor: state.venue?.cashIncrementMinor,
  signedIn: signedIn.staffId === null ? null : { ...signedIn },
  /**
   * READ THROUGH `shiftVersion`, which is the only thing that makes this
   * reactive at all.
   *
   * `shift` is a plain class. Restoring credentials from OPFS mutates it and
   * Vue has no reason to re-render — so a till reloaded during an outage
   * restored a perfectly good credential bundle and went on showing a sign-in
   * screen with NOBODY ON IT, during the outage the whole design exists for.
   *
   * Touching the counter is what turns "the data arrived" into "the screen
   * knows". The same trick `drawerState.version` uses, for the same reason.
   */
  staff: (shiftVersion.value, shift.candidates()),
  loginError: login.error,
  lockedForSeconds: login.lockedForSeconds,
  loginBusy: login.busy,

  onSignIn: ({ staffId, pin }: { staffId: string; pin: string }) => {
    void (async () => {
      login.busy = true;
      const outcome = await shift.signIn(staffId, pin, new Date());
      login.busy = false;
      login.error = outcome.ok ? null : (outcome.messageKey ?? null);
      login.lockedForSeconds = outcome.lockedForSeconds;

      if (outcome.ok) {
        signedIn.staffId = outcome.staffId ?? null;
        signedIn.displayName = outcome.displayName ?? null;
        signedIn.roleId = outcome.roleId ?? null;
        if (signedIn.staffId && signedIn.displayName) {
          holdSignIn(deviceStorage, {
            staffId: signedIn.staffId,
            displayName: signedIn.displayName,
            roleId: signedIn.roleId,
            lastActiveAt: Date.now(),
          });
        }
        // A sign-in that had to enrol has just minted a verifier. Persisting it
        // here rather than inside `enrol` keeps the session the only thing that
        // decides what a bundle contains.
        await persistCredentials();
      }
    })();
  },
  onLocale: (code: string) => {
    void setLocale(code);
  },
  onSignOut: () => {
    /**
     * Signing out LEAVES EVERY CHECK OPEN.
     *
     * A shift change does not settle anybody's bill: the next server picks up
     * the same tables. Closing them here would either strand the money or ask
     * somebody to pay because a colleague went home.
     */
    signOut();
  },

  onOpenTable: (tableRef: string) => {
    void (async () => {
      await book.open(tableRef, "DINE_IN");
      state.activeTable = book.get(tableRef) ? tableRef : tableOf(tableRef);
      state.error = null;
      await refresh();
    })();
  },
  /**
   * A booked party at a free table: it opens with their covers and the
   * booking, and the server marks the booking seated from that event.
   */
  onSeat: ({ bookingId, tableRef }: { bookingId: string; tableRef: string }) => {
    void (async () => {
      const booking = state.bookings.find((b) => b.bookingId === bookingId);
      if (!booking || book.get(tableRef)) return;
      await book.open(tableRef, "DINE_IN", { covers: booking.party, reservationRef: bookingId });
      state.bookings = state.bookings.map((b) => (b.bookingId === bookingId ? { ...b, status: "SEATED", tableRef } : b));
      state.activeTable = tableRef;
      state.error = null;
      await refresh();
    })();
  },
  onBackToFloor: () => {
    // Leaving a table settles nothing and loses nothing — unless nothing was
    // ever rung on it. Then it was opened by mistake, and is let go rather than
    // left on the floor as a ghost nobody can pay.
    void (async () => {
      const table = state.activeTable;
      const session = active();
      if (table && session && session.view().state === "OPEN" && session.view().lines.length === 0) {
        await book.abandon(table);
      }
      state.activeTable = null;
      state.error = null;
      await refresh();
    })();
  },
  /** A sale at the counter: a number, here or to go, then the items. */
  onStartCounter: (mode: "DINE_IN" | "TAKEAWAY") => {
    void (async () => {
      const prefix = counterPrefix();
      if (!prefix) return;
      const code = await (await pickups()).next(prefix);
      await book.openCounter(code, mode);
      state.activeTable = `${PICKUP}${code}`;
      state.error = null;
      await refresh();
    })();
  },
  /** A waiter carried a table's food out. */
  onHandOverTable: (tableRef: string) => {
    const entry = state.ready.find((r) => r.tableRef === tableRef && r.state === "READY");
    if (entry) handOver(entry.orderId);
  },
  /** Handed over at the counter or carried to the table: off the pass. */
  onHandOver: (orderId: string) => handOver(orderId),

  onOpenTab: () => {
    void (async () => {
      const ref = book.nextTabRef();
      await book.open(ref, "DINE_IN");
      state.activeTable = ref;
      state.error = null;
      await refresh();
    })();
  },
  onTransfer: (tableRef: string) => {
    void (async () => {
      const from = state.activeTable;
      if (!from) return;
      if (await book.transfer(from, tableRef)) state.activeTable = tableRef;
      await refresh();
    })();
  },

  onRing: ({ itemRef, choices, note, courseId }: { itemRef: string; choices?: MenuChoice[]; note?: string; courseId?: string }) => {
    const session = active();
    if (!session) return;
    void session
      .ring(itemRef, { ...(choices ? { choices } : {}), ...(note ? { note } : {}), ...(courseId ? { courseId } : {}) })
      .then(applyResult);
  },
  /** "Vyvolat chod 2": the held course goes to the kitchen now. */
  onFireCourse: (courseId: string) => {
    void active()?.fire({ courseId }).then(applyResult);
  },
  onQuantity: ({ selectionId, quantityMilli }: { selectionId: string; quantityMilli: number }) => {
    void active()?.changeQuantity(selectionId, quantityMilli).then(applyResult);
  },
  onVoid: ({
    selectionId,
    reasonCode,
    approval,
  }: {
    selectionId: string;
    reasonCode: VoidReasonCode;
    approval?: { staffId: string; pin: string };
  }) => {
    void (async () => {
      const session = active();
      if (!session) return;
      const line = session.view().lines.find((l) => l.selectionId === selectionId);
      const sent = line !== undefined && line.fulfillmentState !== "NEW" && line.fulfillmentState !== "HELD";
      const approver = await approve(sent ? "VOID_SENT" : "VOID_UNSENT", approval);
      if (approver === null) return;
      await applyResult(await session.voidLine(selectionId, { reasonCode, authorizedBy: approver }));
    })();
  },
  /**
   * Dine-in becomes takeaway. §47 ZDPH moves the rate; the total does not move.
   *
   * The REASON CODE travels rather than the translated words: the log has to
   * mean the same thing whichever language the server had on screen, and a
   * Ukrainian cook's phrasing is not a different fact from a Czech manager's.
   */
  onSetMode: ({
    mode,
    reasonCode,
  }: {
    mode: "DINE_IN" | "TAKEAWAY";
    reasonCode: ModeChangeReasonCode;
  }) => {
    void active()?.setConsumptionMode(mode, { reason: reasonCode }).then(applyResult);
  },
  /**
   * Money off a line — a discount, or a comp, and never the one dressed as the
   * other.
   *
   * `authorizedBy` is filled in by the session from the signed-in actor rather
   * than passed from the screen: a component that could name the authoriser
   * could name somebody else.
   */
  /**
   * Look a company up, through the API.
   *
   * THROUGH THE API rather than directly: `ares.gov.cz` sends no CORS headers,
   * so a fetch from this browser is blocked before it leaves the device — and
   * the rate limit belongs at the venue, which is the unit the Ministry would
   * restrict, not one tablet.
   */
  // The register lookup is lazy: a few times a night at most.
  onLookupBuyer: (registrationId: string) => {
    void import("./buyer-lookup").then((m) =>
      m.lookUpBuyer({ ...config, registrationId }, state, (key) => translatorRef.value.t(key)),
    );
  },

  onIdentifyBuyer: (payload: {
    legalName: string;
    registrationId: string | null;
    vatId: string | null;
    address: string | null;
    source: "ARES" | "MANUAL";
  }) => {
    // Cleared here rather than in the dialog: the next check's lookup must not
    // open pre-filled with the last one's company.
    state.buyerFound = null;
    state.buyerNotice = null;
    void active()?.identifyBuyer(payload).then(applyResult);
  },

  onAllowance: ({
    selectionId,
    kind,
    amountMinor,
    reasonCode,
    approval,
  }: {
    selectionId: string;
    kind: "DISCOUNT" | "COMP";
    amountMinor: bigint;
    reasonCode: AllowanceReasonCode;
    approval?: { staffId: string; pin: string };
  }) => {
    void (async () => {
      const session = active();
      if (!session) return;
      const approver = await approve(kind === "COMP" ? "COMP" : "ALLOWANCE", approval);
      if (approver === null) return;
      await applyResult(
        await session.applyAllowance(selectionId, { kind, amountMinor, reasonCode, authorizedBy: approver }),
      );
    })();
  },
  /**
   * The split, and the way back from it.
   *
   * `selectCheck` is synchronous — it changes which check the session is
   * working on and writes nothing — so it applies its result directly rather
   * than through the promise path the writes use.
   */
  onSelectCheck: (checkId: string) => {
    const session = active();
    if (session) applyResult(session.selectCheck(checkId));
  },
  onSplitEvenly: (ways: number) => {
    void active()?.splitEvenly(ways).then(applyResult);
  },
  onUnsplit: () => {
    void active()?.unsplit().then(applyResult);
  },
  onMoveSelection: ({ selectionId, toCheckId }: { selectionId: string; toCheckId: string }) => {
    void active()?.moveSelection(selectionId, toCheckId).then(applyResult);
  },
  onSend: () => void send(),
  onConfirmAge: () => void confirmAge(),
  onCancelAge: () => {
    // Backing out leaves the line on the check, unfired and unverified. It is
    // still owed, the guest still ordered it, and the next Send asks again —
    // which is the point. Dismissing a prompt is not a way past it.
    state.ageCheck = null;
  },
  onTender: (payload: { kind: TenderKind; offeredMinor?: bigint; tipMinor?: bigint; closeAfter?: boolean }) => {
    void (async () => {
      const session = active();
      if (!session) return;
      // Cash goes into a drawer: with none open here, join the one somebody opened, or say so.
      if (payload.kind === "CASH" && !drawerIsOpen() && !(await (await import("./shift-join")).joinDrawerNow(drawer, state.venue, config))) {
        return applyResult({ ok: false, messageKey: "pos.error.tender.NO_DRAWER" });
      }
      const args = {
        kind: payload.kind,
        ...(payload.offeredMinor !== undefined ? { offeredMinor: payload.offeredMinor } : {}),
        ...(payload.tipMinor !== undefined ? { tipMinor: payload.tipMinor } : {}),
        ...(state.venue?.cashIncrementMinor !== undefined ? { cashIncrementMinor: state.venue.cashIncrementMinor } : {}),
      };
      // AT THE COUNTER: paid, then fired with its number, then closed — one tap.
      const table = state.activeTable;
      if (table?.startsWith(PICKUP) && payload.closeAfter) {
        const paid = await session.payAndFire(args);
        await applyResult(paid);
        if (paid.ok) {
          state.pickupShown = table.slice(PICKUP.length);
          await closeActiveCheck({ ok: true });
          void syncReady();
        }
        return;
      }
      const result = await session.tender(args);
      await applyResult(result);
      // The one-tap path: the balance is taken, so the check closes too.
      if (result.ok && payload.closeAfter && result.value.plan.settles) await closeActiveCheck();
    })();
  },
  onReverseTender: ({ tenderId, reasonCode }: { tenderId: string; reasonCode: string }) => {
    void active()
      ?.voidTender(tenderId, { reasonCode, authorizedBy: actorId() })
      .then(applyResult);
  },
  onOpenReceipts: () => {
    void receipts().then((r) => r.open());
  },
  onCloseReceipts: () => {
    void receipts().then((r) => r.close());
  },
  onSelectReceipt: (checkId: string) => {
    void receipts().then((r) => r.select(checkId));
  },
  onRefundReceipt: (payload: { reasonCode: string; approval?: { staffId: string; pin: string } }) => {
    void receipts().then((r) => r.refund(payload));
  },
  onCloseReceipt: () => {
    state.receipt = null;
    state.pickupShown = null;
  },
  onPrintReceipt: () => {
    void printReceipt();
  },
  onCloseCheck: () => {
    void closeActiveCheck();
  },
  onDismissError: () => {
    state.error = null;
  },

  // --- The drawer ----------------------------------------------------------

  // Always CLOSE. The sheet is identical either way — twelve denominations at
  // zero — and the kind only labels the record that results.
  drawerSheet: drawer.countSheet("CLOSE"),
  drawerOpen: drawerIsOpen(),
  drawerReport: drawer.report(),
  /**
   * SALES THIS TILL HAS NOT YET SENT, from the only fact it actually has.
   *
   * The Z-report has had this line, its copy in four languages and four passing
   * tests since the drawer screens were written, and NOTHING EVER SUPPLIED IT —
   * so the one number a Czech owner looks for at close of business could never
   * render.
   *
   * It is the outbox depth, not a fiscal queue depth, and the distinction is
   * the honest one: a till knows what it has failed to HAND OVER. Whether the
   * server has since reported a sale to the Financial Administration is the
   * server's fact, and it is on the back office's Daně screen. Claiming to know
   * it here would be a till reporting on a system it cannot see.
   */
  fiscalOwed: state.queuedCount,
  // The signed-in person's NAME for the Z-report. The report itself carries the
  // staff id, because that is what the events are attributed to; a document a
  // manager signs should say who they are.
  cashierName: signedIn.displayName,
  /**
   * The code for the check on screen, MINTED LOCALLY.
   *
   * Null without a verified config bundle, because the venue key that signs it
   * lives in the bundle — a till that has never synced genuinely cannot mint
   * one, and that is the honest state rather than an empty square.
   *
   * Read from a cache rather than awaited, because a prop cannot be a promise
   * and the HMAC is asynchronous. `refreshGuestUrl` fills it whenever the check
   * changes; until it lands the button is simply disabled.
   */
  guestUrl: guestState.url,
  drawerError: drawerState.error,
  drawerStale: drawerState.stale,
  clock: state.venue?.clock ?? null,
  training: state.venue?.training ?? false,
  drawerBusy: drawerState.busy,

  onOpenDrawer: (openingFloat: readonly DenominationCount[]) => {
    void (async () => {
      drawerState.busy = true;
      await applyDrawer(
        await drawer.openShift({
          // One shared till per device for now. IMPREST and SERVER_BANKING are
          // modelled and choosing between them is a venue setting that belongs
          // in the config bundle, not a guess made here.
          drawerModel: "SHARED_TILL",
          // The VENUE's drawer, from the signed bundle. Falling back to the
          // device id keeps a venue that has not named its drawers working,
          // with a uuid on the Z-report rather than a wrong name.
          drawerId: state.venue?.drawerId ?? config.deviceId,
          cashierId: actorId(),
          openingFloat,
        }),
      );
      drawerState.busy = false;
    })();
  },
  onCountDrawer: (denominations: readonly DenominationCount[]) => {
    void (async () => {
      drawerState.busy = true;
      const counted = await drawer.count({ kind: "CLOSE", denominations, countedBy: actorId() });
      // The other tills' cash in this drawer, for the Z — after the count, so
      // it stays blind; lazy, because it runs once a shift.
      if (counted.ok) await (await import("./shift-join")).addOtherTills(drawer, config);
      await applyDrawer(counted);
      drawerState.busy = false;
    })();
  },
  onPayOut: ({ amountMinor, reasonCode }: { amountMinor: bigint; reasonCode: string }) => {
    void (async () => {
      drawerState.busy = true;
      await applyDrawer(await drawer.move({ kind: "PAY_OUT", amountMinor, reasonCode }));
      drawerState.busy = false;
    })();
  },
  onCloseStale: () => {
    void (async () => {
      drawerState.busy = true;
      await applyDrawer(await (await import("./shift-join")).adoptStaleShift(drawer, drawerState, config));
      drawerState.busy = false;
    })();
  },
  onCloseDrawer: () => {
    void (async () => {
      drawerState.busy = true;
      await applyDrawer(await drawer.closeShift());
      drawerState.busy = false;
    })();
  },
  };
};


// --- The menu ---------------------------------------------------------------

/**
 * Fetch the menu and offer it to every open check.
 *
 * `offerMenu` already implements the rule that matters: a newer version is
 * STAGED while a check is open, so a line already rung cannot reprice under the
 * person holding the handheld. It simply never had a caller.
 *
 * A FAILURE IS NOT FATAL and does not clear what we have. That is the whole
 * point of an edge-resident pre-taxed snapshot: the till keeps selling from the
 * menu it already holds. It is logged rather than shown, because a server who
 * cannot fix it does not need a banner about it — the outbox depth and the
 * sell-state banner are what tell them something is wrong.
 */
/**
 * Lease receipt numbers while there is a network to lease them over.
 *
 * Nothing called this for as long as `ReceiptNumbers` existed, so a till with
 * working storage held no block and refused EVERY close with
 * `pos.error.noReceiptNumber` — while opening tables, ringing and firing all
 * worked, which is exactly the shape that hides it. See `numbers-sync.ts`.
 */
async function syncNumbers(): Promise<void> {
  const result = await syncNumbers_(receiptNumbers);
  if (result.messageKey) state.error = { key: result.messageKey };
}


/**
 * FOUR TIMES THE MENU'S CADENCE, and separately from it.
 *
 * The menu is versioned and frozen; this is not, and it is small — one request
 * carrying only what is off. That separation is the whole design: folding
 * availability into the menu would mean a new immutable version every time a
 * dish ran out, and a till refetching a whole snapshot to learn it.
 *
 * Fifteen seconds is the answer to "how long may a server go on offering
 * something the pass has run out of", which is a different question from how
 * stale a price may be.
 */
async function syncAvailability(): Promise<void> {
  try {
    const off = await fetchAvailability({
      baseUrl: config.baseUrl,
      apiKey: config.apiKey,
      venueId: config.venueId,
    });

    const refs = new Set(off.map((entry) => entry.ref));
    // Mutated in place rather than replaced: the set is reactive and handed to
    // sessions as a live getter, and swapping the object would leave every one
    // of them reading the old one.
    for (const ref of [...soldOut]) if (!refs.has(ref)) soldOut.delete(ref);
    for (const ref of refs) soldOut.add(ref);
  } catch (error) {
    /**
     * KEEPS WHAT IT HAS. An unreachable server is not an empty list — treating
     * it as one would put every 86'd dish back on the grid the moment the wifi
     * wobbled, which is the failure direction that sells food nobody has.
     */
    console.warn("availability: keeping what is on screen —", (error as Error).message);
  }
}

async function syncMenu(): Promise<void> {
  try {
    const fetched = await fetchMenu({
      baseUrl: config.baseUrl,
      apiKey: config.apiKey,
      venueId: config.venueId,
    });

    if (fetched.version === menuState.current.version) return;

    book.offerMenu(fetched);
    // Replaced WHOLESALE rather than mutated field by field, so a half-applied
    // menu — new prices against old items — is never on screen for a frame.
    menuState.current = fetched;
    await storage.cache?.put("menu", fetched, fetched.version, new Date());
  } catch (error) {
    console.warn("menu: keeping the cached copy —", (error as Error).message);
  }
}

// --- Configuration ----------------------------------------------------------

let configCache: Awaited<ReturnType<typeof createConfigCache>> | null = null;

/**
 * Fetch the signed bundle and install it if it verifies.
 *
 * A failure keeps what we have, exactly like the menu — that is the point of a
 * signed, offline-cacheable bundle. What changes is that the till now KNOWS how
 * old its configuration is, so `sellState` can report CONFIG_STALE while it is
 * still selling and CONFIG_EXPIRED once the grace has run out.
 */
async function syncConfig(): Promise<void> {
  try {
    configCache ??= await createConfigCache({
      publicKeyRaw: config.configPublicKey,
      tenantId: config.tenantId,
      venueId: config.venueId,
    });

    const signed = await fetchConfigBundle({
      baseUrl: config.baseUrl,
      apiKey: config.apiKey,
      venueId: config.venueId,
    });

    const result = await configCache.accept(signed, new Date());
    if (!result.accepted) {
      // A refusal is not a network problem and must not look like one. A
      // BAD_SIGNATURE here means somebody on the venue's LAN is answering for
      // the platform, and that is worth a line in the log even though the till
      // carries on with the bundle it trusts.
      console.warn(`config: refused a bundle — ${result.reason}`);
      return;
    }

    const bundle = configCache.current();
    if (bundle) {
      state.venue = readVenueConfig(bundle.payload);
      // Reservations may have just been switched on, or off.
      void syncBookings();
      // The SIGNED bundle, so restoring it re-verifies exactly as accepting it
      // from the network does. A bundle edited on disk verifies against nothing.
      await storage.cache?.put("config", signed, bundle.version, new Date());
    }
  } catch (error) {
    // UNPAIRED from the office: the key is revoked. Back to the code screen —
    // a till that looks offline forever is worse. Never for a development build.
    if (error instanceof ConfigFetchError && error.status === 401 && !import.meta.env["VITE_GF_API_KEY"]) {
      forgetPairing(pairingStore, "till");
      location.reload();
      return;
    }
    console.warn("config: keeping the cached copy —", (error as Error).message);
  }
}

// --- Draining ---------------------------------------------------------------

const upload = createUploader({
  baseUrl: config.baseUrl,
  apiKey: config.apiKey,
  venueId: config.venueId,
  deviceId: config.deviceId,
});

/**
 * One batch per tick, with backoff.
 *
 * `Outbox.drain()` takes ONE batch per call by design — the caller owns the
 * cadence, because a self-looping drain would burn an event's whole attempt
 * budget inside a single call and turn one transient hiccup into a quarantine.
 *
 * The loop itself lives in `@gf/sync-client` now, with the two bugs it was
 * repaired for written into its header and pinned by a spec that drives a fake
 * clock. It used to live here, and a copy of it lived in the kitchen screen —
 * the copy from before the repair, which is how the kitchen inherited a bug the
 * till had already fixed. Neither copy had a test, because neither could have
 * one: this module also mounts a Vue app.
 */
const drainLoop = createDrainLoop({
  drain: () => outbox.drain(upload),
  afterDrain: refresh,
  isOnline: () => navigator.onLine,
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (id) => window.clearTimeout(id),
});

// --- Start ------------------------------------------------------------------

/**
 * Rebuild the open shift BEFORE anything else runs.
 *
 * A till reloaded mid-service has a drawer that was counted in hours ago, and
 * every tender since. Without this the Z-report says the evening took nothing.
 */
void drawer.restore().then(() => {
  drawerState.version += 1;
});

/**
 * TAKE BACK THE FLOOR the server already has open.
 *
 * The book of tables is a `Map` in this tab. A reload — a refresh, a tablet
 * waking up, a browser updating itself — used to lose every one of them, and
 * tapping the table again opened a SECOND order on it. A walkthrough that
 * reloaded the till twice left FOUR open orders on table 1, three of them
 * empty, while the floor plan read "0 of 11 occupied".
 *
 * After the venue config, because the business date comes from the venue's
 * clock and a till must not ask the server about the browser's idea of today.
 * Failure is not an empty floor: the tables stay unknown and the next cycle
 * tries again, because a till that decided nothing was open would let a server
 * seat somebody on top of an unpaid bill.
 */
void refresh();
// Before anything that needs a network: a till that starts offline must still
// be able to sign in whoever has already enrolled on it.
void restoreCredentials();
/**
 * BEFORE the network calls below, not after.
 *
 * They all fail during an outage and each one logs "keeping the cached copy" —
 * which is only true if something put a copy there first.
 */
void restoreOfflineCache();
void registerServiceWorker();
// Storage the browser may not clear when the disk fills: the outbox holds sales not yet sent.
void navigator.storage?.persist?.().catch(() => false);
// What breaks on this till reaches the server's log.
void import("@gf/sync-client/error-report").then(({ reportErrors, postReports }) =>
  reportErrors({
    target: window,
    where: () => location.pathname + location.hash,
    send: postReports({ baseUrl: apiBase, app: "pos", apiKey: () => paired.apiKey }),
  }),
);
void syncConfig();
void syncShift();
void syncMenu();
// After the config lands, so the venue's clock decides which business day to
// ask about. `syncConfig` sets it; this waits for it rather than racing.
/**
 * Held, because reconnecting asks the server again.
 *
 * `null` until the config lands and the chunk loads. The `online` handler
 * below fires on a till that has just come back, which is precisely when the
 * server has things to tell it — a table another device opened, or one it
 * settled, while this till was away.
 */
let floorRestore: Awaited<
  ReturnType<typeof import("./floor-restore")["createFloorRestore"]>
> | null = null;

void (async () => {
  for (let i = 0; i < 40 && !state.venue?.clock; i++) await new Promise((r) => setTimeout(r, 250));
  const clock = state.venue?.clock;
  if (!clock) return;

  /**
   * LAZILY IMPORTED, because none of it is needed to paint the till.
   *
   * Every line of the restore runs before anybody has signed in, so it has no
   * business in the bundle a server waits for at the start of a shift. The
   * service worker precaches the chunk, so this resolves during an outage
   * exactly as it does online — which the offline rehearsal exercises.
   */
  const { createFloorRestore } = await import("./floor-restore");
  const restore = createFloorRestore({
    book,
    journal: storage.orders,
    cache: storage.cache,
    wire: { baseUrl: config.baseUrl, apiKey: config.apiKey, venueId: config.venueId },
    businessDate: () => businessDateOf(new Date(), clock),
    refresh,
  });

  floorRestore = restore;

  // This device's own memory first — it needs no network and it is instant.
  await restore.journalled();
  // Then the floor the server last showed us, which is the only way this till
  // knows about a table another device has open.
  await restore.cached();
  // Then the server, which is authoritative and merges over the top.
  await restore.fromServer();

  // And the drawer: join the shift already open on it rather than opening a
  // second one on the same box of money.
  await adoptOpenShift();

  /**
   * DEVICES THAT AGREE. The venue's stream says the moment another device
   * moves an order — a round on a phone, a guest's beer, a table paid at the
   * bar — and the floor is read then, not at the next poll. Two servers opening
   * one table inside a thirty-second window was two orders on it. On every
   * (re)connect the floor is read too, to catch up on what was missed.
   */
  const ORDER_TYPES = /^(order|check|selection|tender|allowance|receipt)\./;
  let queued: number | null = null;
  const readFloorSoon = (): void => {
    queued ??= window.setTimeout(() => {
      queued = null;
      void restore.fromServer();
    }, 250);
  };
  const { followVenue } = await import("@gf/sync-client/venue-stream");
  followVenue({
    baseUrl: config.baseUrl,
    apiKey: config.apiKey,
    venueId: config.venueId,
    onOpen: readFloorSoon,
    onChange: (change) => {
      if (change.types.some((t) => ORDER_TYPES.test(t))) readFloorSoon();
      // A ticket bumped, recalled or handed over: what is ready has moved.
      if (change.types.some((t) => t.startsWith("ticket.") || t === "selection.progressed")) void syncReady();
      // A new floor or company from the office: fetch the bundle now.
      if (change.types.includes("config.published")) void syncConfig();
    },
    // Unpaired in the office: the config sync hears the same 401 and goes back to pairing.
    onRefused: () => void syncConfig(),
  });
})();
void syncNumbers();
drainLoop.start();

/**
 * A MINUTE, not five.
 *
 * The old cadence said a price change "should reach the floor within the hour",
 * which is a defensible thing to say about a menu and the wrong answer for the
 * two cases that actually happen: a price typed wrong and corrected, and a dish
 * that has just run out. Both want the floor to know now, and a manager who
 * publishes and then watches four tills go on selling the old price will
 * publish again, and again, before concluding the button does not work.
 *
 * It costs a small JSON fetch per till per minute, and the response is
 * unchanged almost every time. `syncMenu` already compares versions and returns
 * early, so an unchanged menu is not re-applied and nothing re-renders.
 */
window.setInterval(() => void syncMenu(), 60_000);
window.setInterval(() => void syncAvailability(), 15_000);
window.setInterval(() => void syncReady(), 20_000);
void syncReady();
window.setInterval(() => void syncBookings(), 60_000);
void syncBookings();
// Config on a slower cadence than the menu: a floor plan changes when a room is
// rearranged, a price changes at a daypart boundary.
window.setInterval(() => void syncConfig(), 30 * 60_000);
/**
 * Numbers on the MENU's cadence, not the config's.
 *
 * A block is spent by trading, so the rate that matters is covers per hour, not
 * how often somebody edits the floor plan. Five minutes tops up long before a
 * service can burn through the tail of a 200-number block.
 */
window.setInterval(() => void syncNumbers(), 5 * 60_000);

/**
 * THE FLOOR, ON A TIMER — and it was the one sync that never had one.
 *
 * `fromServer()` ran at startup and on the `online` event and nowhere else,
 * while the menu, the config and the numbering blocks all polled. So a till that
 * had been sitting on the pass all evening, online the whole time, never learned
 * anything it had not written itself.
 *
 * That is not hypothetical any more: a guest's phone can put a line on a check.
 * A server walks over to tender the table, and the beer somebody has already
 * drunk is not on the bill. The guest is happy; the venue is short.
 *
 * THIRTY SECONDS, which is affordable because the list is now cheap to ask for.
 * `/v1/checks` publishes a per-order high-water mark, and the events are fetched
 * only for orders that have actually moved — so an idle floor costs one small
 * request, not one per open table. A guest expects the person carrying the food
 * to know what they ordered, and half a minute is about the edge of that.
 *
 * Safe to repeat because `adopt` merges rather than replaces: a line this till
 * rang and has not drained is not in the server's answer, and folding a set that
 * does not contain it cannot remove it.
 */
window.setInterval(() => void floorRestore?.fromServer(), 30_000);

window.addEventListener("online", () => {
  /**
   * DRAIN NOW, not at the end of whatever delay was already running.
   *
   * Resetting the backoff alone does nothing to a timer that has already been
   * scheduled — which is why a reconnected till used to sit on its outbox for
   * up to a minute with the banner reading "Připojeno". `reconnected()` cancels
   * the pending attempt and goes immediately, and if a drain happens to be in
   * flight it goes again the moment that one lands rather than running two at
   * once against a queue whose whole guarantee is its order.
   */
  drainLoop.reconnected();
  void syncConfig();
  void syncShift();
  void syncMenu();
  void syncAvailability();
  // Null only if the config never landed, in which case there is no business
  // day to ask about either.
  void floorRestore?.fromServer();
  // A till that has been offline has been SPENDING and cannot have leased;
  // this is the moment it can.
  void syncNumbers();
});

// --- Mount, LAST ------------------------------------------------------------

/**
 * THE VERY LAST STATEMENT IN THE MODULE, and that is load-bearing.
 *
 * `mount` renders synchronously, so every value the first render touches must
 * already be initialised. `screenProps` reads `condition()`, which reads
 * `configCache` — a `let` declared further down this file. Mounting before it
 * threw "Cannot access 'configCache' before initialization" in the temporal
 * dead zone, killing the module and leaving a BLANK TILL in a real browser, on
 * every single load.
 *
 * Nothing in CI saw it. Component tests mount components with plain props; the
 * bundle gate builds the artifact and never runs it; the browser e2e drives
 * `window.__gf`, which the module sets up before this line and which therefore
 * survived. Only opening the page catches this class of fault, and that is now
 * `e2e/browser/console-and-till.spec.ts`.
 */
const vueApp = createApp({ render: () => h(PosScreen, screenProps()) });
// Vue keeps a render error to itself; handed on, the error report hears it.
vueApp.config.errorHandler = (error) => {
  console.error(error);
  dispatchEvent(new ErrorEvent("error", { error }));
};
vueApp.mount("#app");

/**
 * The drawer's and the receipt's words, AFTER the first paint.
 *
 * Both screens are lazily imported and their COPY was not — four kilobytes of
 * an eight-kilobyte Czech catalogue, downloaded at the start of every shift to
 * be used twice a day.
 *
 * MERGED INTO `heldCatalogs` RATHER THAN INTO THE LIVE TRANSLATOR, and that is
 * the whole reason this is five lines instead of two. `switchLocale` REBUILDS
 * the translator from `heldCatalogs` when somebody picks a language, so
 * anything added to the instance alone would vanish the first time a Ukrainian
 * cook took over the till — and vanish silently, on the two screens nobody
 * opens until the end of a shift.
 *
 * FIRED HERE RATHER THAN WHEN A SCREEN OPENS, deliberately. Which screen is up
 * is a `ref` inside a component, and a component reaching into i18n loading for
 * its own words is a dependency pointing the wrong way. After mount is off the
 * critical path — which is what the bundle budget measures — and the service
 * worker precaches the chunk like every other, so a till that has been offline
 * since its last update still counts down and prints an účtenka.
 *
 * Not awaited and not fatal: a till whose deferred copy failed shows raw keys
 * on two screens and goes on selling, which is the right way round.
 */
void loadDeferredCatalogs(localeRef.value)
  .then((deferred) => {
    for (const addition of deferred) {
      const held = heldCatalogs.get(addition.locale);
      heldCatalogs.set(addition.locale, {
        locale: addition.locale,
        messages: { ...(held?.messages ?? {}), ...addition.messages },
      });
    }

    const fallback = heldCatalogs.get(FALLBACK_LOCALE);
    const active = heldCatalogs.get(localeRef.value);
    // The fallback FIRST, the order `switchLocale` uses and the order
    // `Translator` resolves in.
    translatorRef.value = buildTranslator(
      localeRef.value === FALLBACK_LOCALE || !fallback
        ? [active ?? fallback!]
        : [fallback, active ?? fallback],
      localeRef.value,
    );
  })
  .catch((error: unknown) => {
    console.warn("i18n: the drawer and receipt copy did not load —", (error as Error).message);
  });
